import { useEffect, useState } from "react";
import { Boxes } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import { AccessorioMagazzino, TipoAccessorio, TIPO_ACCESSORIO_LABELS, FornitoreOption } from "../../api/magazzino";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";

/**
 * Registra o modifica una riga di materiale di consumo.
 *
 * ## L'intervallo di matricole compare solo per i sacchi
 *
 * I sacchi arrivano numerati in sequenza dal fornitore, e di quel lotto si
 * registrano i due estremi. Cassetti e accettatori **non hanno un seriale
 * tracciato**: per loro i due campi non si nascondono soltanto, non vengono
 * proprio mandati — così un cambio di tipo su una riga esistente cancella
 * l'intervallo invece di lasciarlo appeso a un tipo che non lo ammette.
 *
 * Restano **facoltativi** anche sui sacchi: una riga può essere solo una
 * quantità. La regola completa (entrambi o nessuno, almeno 3 caratteri
 * alfanumerici per estremo) vive nel backend, in `utils/accessori.ts`; qui non
 * si riscrive, si evita solo di mandare campi che non c'entrano.
 *
 * ## Il fornitore
 *
 * Elenco chiuso dei contatti con `tipo_contatto = fornitore`. Se è vuoto non è
 * un guasto: vuol dire che in anagrafica nessuno è ancora classificato così, e
 * il campo lo dice invece di mostrare una tendina muta.
 */
export function AccessorioForm({
  accessorio,
  onChiudi,
  onSalvato,
}: {
  /** Assente = nuova riga. */
  accessorio?: AccessorioMagazzino;
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const { showToast } = useToast();
  const inModifica = Boolean(accessorio);

  const [tipo, setTipo] = useState<TipoAccessorio>(accessorio?.tipo_accessorio ?? "sacco_cassaforte");
  const [quantita, setQuantita] = useState(String(accessorio?.quantita_disponibile ?? 0));
  const [matricolaDa, setMatricolaDa] = useState(accessorio?.matricola_da ?? "");
  const [matricolaA, setMatricolaA] = useState(accessorio?.matricola_a ?? "");
  const [fornitoreId, setFornitoreId] = useState(accessorio?.fornitore?.id ?? "");
  const [note, setNote] = useState(accessorio?.note ?? "");

  const [fornitori, setFornitori] = useState<FornitoreOption[]>([]);
  const [fornitoriCaricati, setFornitoriCaricati] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    magazzinoApi
      .listFornitori()
      .then(({ fornitori: f }) => setFornitori(f))
      // Senza opzioni il campo resta scegliibile a vuoto: il fornitore è
      // facoltativo, e un elenco che non si carica non deve bloccare il resto.
      .catch(() => setFornitori([]))
      .finally(() => setFornitoriCaricati(true));
  }, []);

  const conIntervallo = tipo === "sacco_cassaforte";

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setErrore(null);

    const q = Number(quantita);
    // `Number("")` vale 0, che è una quantità plausibile: il vuoto va escluso
    // prima della conversione, non dopo.
    if (quantita.trim() === "" || !Number.isInteger(q) || q < 0) {
      setErrore("La quantità dev'essere un numero intero maggiore o uguale a zero.");
      return;
    }

    const payload: magazzinoApi.AccessorioPayload = {
      tipo_accessorio: tipo,
      quantita_disponibile: q,
      fornitore_id: fornitoreId || null,
      note: note.trim() || null,
      // Espliciti a `null` e non omessi: in modifica, passare da sacco a
      // cassetto deve azzerare l'intervallo, non lasciarlo com'era.
      matricola_da: conIntervallo ? matricolaDa.trim() || null : null,
      matricola_a: conIntervallo ? matricolaA.trim() || null : null,
    };

    setInCorso(true);
    try {
      if (accessorio) await magazzinoApi.aggiornaAccessorio(accessorio.id, payload);
      else await magazzinoApi.creaAccessorio(payload);
      showToast(inModifica ? "Accessorio aggiornato." : "Accessorio registrato.", "success");
      onSalvato();
      onChiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile salvare l'accessorio."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title={inModifica ? "Modifica accessorio" : "Nuovo accessorio"}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-accessorio-magazzino" loading={inCorso}>
            <Boxes className="h-4 w-4" aria-hidden="true" />
            {inModifica ? "Salva" : "Registra"}
          </Button>
        </div>
      }
    >
      <form id="form-accessorio-magazzino" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="acc-tipo" className="text-sm font-medium text-ink">
              Tipo di accessorio
            </label>
            <select
              id="acc-tipo"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoAccessorio)}
            >
              {(Object.keys(TIPO_ACCESSORIO_LABELS) as TipoAccessorio[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_ACCESSORIO_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Quantità disponibile"
            type="number"
            min={0}
            step={1}
            value={quantita}
            onChange={(e) => setQuantita(e.target.value)}
            required
          />
        </div>

        {/* Solo per i sacchi. Non è un campo disabilitato: per gli altri tipi
            l'intervallo non esiste, e mostrarlo spento suggerirebbe che
            esista e che manchi qualcosa. */}
        {conIntervallo && (
          <fieldset className="flex flex-col gap-3 rounded-lg border border-line bg-line/10 p-3">
            <legend className="px-1 text-sm font-medium text-ink">Lotto (facoltativo)</legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Dalla matricola" value={matricolaDa} onChange={(e) => setMatricolaDa(e.target.value)} />
              <Input label="Alla matricola" value={matricolaA} onChange={(e) => setMatricolaA(e.target.value)} />
            </div>
            <p className="text-xs text-ink/45">
              Sono i due estremi di un lotto numerato dal fornitore, non due pezzi censiti singolarmente. Vanno indicati
              entrambi o nessuno, con almeno 3 caratteri alfanumerici ciascuno.
            </p>
          </fieldset>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="acc-fornitore" className="text-sm font-medium text-ink">
            Fornitore
          </label>
          <select
            id="acc-fornitore"
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            value={fornitoreId}
            onChange={(e) => setFornitoreId(e.target.value)}
          >
            <option value="">Non indicato</option>
            {fornitori.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nome}
              </option>
            ))}
          </select>
          {fornitoriCaricati && fornitori.length === 0 && (
            <p className="text-xs text-ink/50">
              Nessun contatto è classificato come fornitore in anagrafica. Si imposta dalla scheda del contatto, campo
              «Tipo contatto».
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="acc-note" className="text-sm font-medium text-ink">
            Note
          </label>
          <textarea
            id="acc-note"
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
